import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Map as MapLibreMap, GeoJSONSource, ImageSource } from 'maplibre-gl'
import { Alert, Box, Button, Snackbar, TextField, Typography } from '@mui/material'
import { ArrowLeft, ScissorsLineDashed, TriangleAlert, Wrench } from 'lucide-react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useNavigate } from '../lib/router'
import { AppShell, Crumb, HomeCrumb } from '../components/AppShell'
import {
  useExcludePlot, useIssues, usePlot, useReferenceLayers, useRepairPlot, useRun, useTrimPlot, useUpdatePlot,
} from '../api/hooks'
import { get } from '../api/client'
import { BasemapSwitch, MapCanvas, MapControl, fitToFeatures, forEachPosition } from '../components/MapCanvas'
import { Eyebrow, Mono, StatusPill } from '../components/primitives'
import { SkeletonScreen } from '../components/Skeleton'
import { useNavigationGuard, useUnsavedWork } from '../components/NavigationGuard'
import { ha, num } from '../lib/format'
import { STATUS_COLOURS, tokens } from '../theme'
import type { ReferenceLayer } from '../api/types'
import { PlotComments } from './inspect/PlotComments'
import { ReferenceFields } from '../components/ReferenceFields'
import { AttributeName } from '../components/AttributeName'
import { RunSummaryPanel } from './results/RunSummaryPanel'
import { FarmerPicker, FoPicker, SupplierPicker, type RecordOption } from '../components/pickers'

type Overlay = 'map' | 'satellite' | 'landuse'

const EMPTY: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] }
/** A 1×1 transparent PNG: an image source needs a picture before the first real one arrives. */
const TRANSPARENT_PIXEL =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg=='

const SRC = {
  neighbours: 'inspect-neighbours',
  subject: 'inspect-subject',
  overlaps: 'inspect-overlaps',
  vertices: 'inspect-vertices',
  errors: 'inspect-errors',
  preview: 'inspect-preview',
  landuse: 'inspect-landuse',
} as const

/** The eight attributes, in EUDR template order. */
const ATTRIBUTES = [
  { field: 'CO', key: 'co' },
  { field: 'SU', key: 'su' },
  { field: 'FO', key: 'fo' },
  { field: 'FO_ID', key: 'fo_id' },
  { field: 'Farmer_ID', key: 'farmer_id' },
  { field: 'Plot_ID', key: 'plot_id' },
  { field: 'Yield', key: 'yield_kg' },
  { field: 'Certificate', key: 'certificate' },
] as const

type AttributeKey = (typeof ATTRIBUTES)[number]['key']

const NO_LAYERS: ReferenceLayer[] = []

/** Screen 1d — one polygon, its geometry, its attributes and the fixes. */
export function InspectPlot() {
  const { runId, plotId } = useParams<{ runId: string; plotId: string }>()
  const navigate = useNavigate()
  // The checks the review was narrowed to (?check=a,b), carried from the
  // region's map or table so "1 of 25" duplicates stays 25 here, not every
  // issue in the run.
  const [params, setParams] = useSearchParams()
  const checkParam = params.get('check') ?? ''
  const checkFilter = useMemo(() => checkParam.split(',').filter(Boolean), [checkParam])
  const keep = checkParam ? `?check=${encodeURIComponent(checkParam)}` : ''

  const { data: run } = useRun(runId)
  // The plot on screen stays until the next one arrives (`switching`), so
  // stepping through plots changes the contents, not the whole screen.
  const { data: detail, isLoading, isPlaceholderData: switching } = usePlot(plotId, runId, { keepPrevious: true })
  const { data: issueList } = useIssues(runId, { limit: '1000', check: checkParam || undefined })
  // A fresh [] each render while loading re-ran the land-use effect every render.
  const { data: layers = NO_LAYERS } = useReferenceLayers()

  const repair = useRepairPlot(runId)
  const trim = useTrimPlot(runId)
  const update = useUpdatePlot(runId)
  const exclude = useExcludePlot(runId)

  const { go: goBack } = useNavigationGuard()
  const mapRef = useRef<MapLibreMap | null>(null)
  const [ready, setReady] = useState(false)
  const [overlay, setOverlay] = useState<Overlay>('map')
  const [previewRepair, setPreviewRepair] = useState(false)
  const [previewGeometry, setPreviewGeometry] = useState<GeoJSON.Geometry | null>(null)
  const [previewDelta, setPreviewDelta] = useState<number | null>(null)
  const [edits, setEdits] = useState<Partial<Record<AttributeKey, string>>>({})
  // The host's own references changed here, not saved yet; null removes one.
  const [refEdits, setRefEdits] = useState<Record<string, string | null>>({})
  // An FO_ID for a farmer organisation that has none, not saved yet.
  const [foCode, setFoCode] = useState('')
  // Records chosen in the pickers, not saved yet. undefined = unchanged,
  // null = unlinked.
  const [recordEdits, setRecordEdits] = useState<{
    supplier?: RecordOption | null; fo?: RecordOption | null; farmer?: RecordOption | null
  }>({})
  const [notice, setNotice] = useState<string | null>(null)

  const plot = detail?.plot
  // Back to the region the plot is in, whose page shows the run's results.
  // Opened on its own (/plots/:id, a supplier's or farmer's list), it leads
  // home instead.
  const backTo = runId ? (plot ? `/regions/${plot.region_id}${keep}` : `/checks/runs/${runId}`) : '/'
  // A new plot starts clean: no unsaved edits, no repair preview of the last one.
  useEffect(() => {
    setEdits({})
    setRecordEdits({})
    setRefEdits({})
    setFoCode('')
    setPreviewRepair(false)
  }, [plotId])

  // "Issue 14 of 135" — position within the run's whole issue list.
  const issues = issueList?.issues ?? []
  const orderedPlots = useMemo(() => {
    const seen = new Set<string>()
    return issues.filter((i) => (seen.has(i.plot_uuid) ? false : (seen.add(i.plot_uuid), true)))
  }, [issues])
  const index = orderedPlots.findIndex((i) => i.plot_uuid === plotId)
  const goTo = (offset: number) => {
    const next = orderedPlots[index + offset]
    if (next) navigate(`/checks/runs/${runId}/plots/${next.plot_uuid}${keep}`)
  }

  /**
   * Where to go once this plot is dealt with: the next plot with issues, or
   * the results when there is none. Worked out before the action, not after —
   * excluding or reviewing a plot can take it out of the issue list, and the
   * list read afterwards no longer says where this plot was.
   */
  const nextStop = () => {
    const next = index >= 0 ? orderedPlots[index + 1] : undefined
    return next ? `/checks/runs/${runId}/plots/${next.plot_uuid}${keep}` : backTo
  }

  /**
   * One action per plot. The address changes at once but the screen catches
   * up a frame or two later, and in between the old plot's buttons are still
   * there and live: a quick second click acted on the plot just dealt with.
   * A ref, not state, because it has to hold within the same frame.
   */
  const leaving = useRef(false)
  useEffect(() => { leaving.current = false }, [plotId])
  const leave = () => {
    if (leaving.current) return false
    leaving.current = true
    return true
  }

  /**
   * Done with this plot: save, then go to the next one. Moving on before the
   * answer meant a failed save had already thrown the edits away, so the
   * fields had to be filled in again (AG-81). If it fails, stay on the plot
   * with the edits as they were and say why.
   */
  const actAndMoveOn = (done: string, request: () => Promise<unknown>) => {
    if (!leave()) return
    const to = nextStop()
    request()
      .then(() => {
        setNotice(done)
        navigate(to)
      })
      .catch((err: Error) => {
        leaving.current = false
        setNotice(`Not saved: ${err.message}`)
      })
  }

  const geometryErrorIssue = detail?.issues.find((i) => i.check_key === 'geometry_errors')

  // --- map ---------------------------------------------------------------
  const onReady = useCallback((map: MapLibreMap) => {
    mapRef.current = map

    // Land use is a raster: the API renders the visible window as a
    // coloured PNG, drawn here as an image over exactly that box.
    map.addSource(SRC.landuse, {
      type: 'image',
      url: TRANSPARENT_PIXEL,
      coordinates: [[0, 0.0001], [0.0001, 0.0001], [0.0001, 0], [0, 0]],
    })
    map.addLayer({
      id: `${SRC.landuse}-fill`,
      type: 'raster',
      source: SRC.landuse,
      layout: { visibility: 'none' },
      paint: { 'raster-opacity': 1, 'raster-resampling': 'nearest' },
    })

    map.addSource(SRC.neighbours, { type: 'geojson', data: EMPTY })
    map.addLayer({
      id: `${SRC.neighbours}-fill`,
      type: 'fill',
      source: SRC.neighbours,
      paint: { 'fill-color': 'rgba(96,190,148,0.20)' },
    })
    map.addLayer({
      id: `${SRC.neighbours}-line`,
      type: 'line',
      source: SRC.neighbours,
      paint: { 'line-color': tokens.color.brandGreen700, 'line-width': 1.2 },
    })

    map.addSource(SRC.subject, { type: 'geojson', data: EMPTY })
    map.addLayer({
      id: `${SRC.subject}-fill`,
      type: 'fill',
      source: SRC.subject,
      paint: {
        'fill-color': [
          'match', ['get', 'status'],
          'passed', STATUS_COLOURS.passed.fill,
          'flagged', STATUS_COLOURS.flagged.fill,
          'rejected', STATUS_COLOURS.rejected.fill,
          'excluded', STATUS_COLOURS.excluded.fill,
          STATUS_COLOURS.flagged.fill,
        ] as never,
      },
    })
    map.addLayer({
      id: `${SRC.subject}-line`,
      type: 'line',
      source: SRC.subject,
      paint: {
        'line-color': [
          'match', ['get', 'status'],
          'passed', STATUS_COLOURS.passed.stroke,
          'flagged', STATUS_COLOURS.flagged.stroke,
          'rejected', STATUS_COLOURS.rejected.stroke,
          'excluded', STATUS_COLOURS.excluded.stroke,
          STATUS_COLOURS.flagged.stroke,
        ] as never,
        'line-width': 2,
      },
    })

    map.addSource(SRC.overlaps, { type: 'geojson', data: EMPTY })
    map.addLayer({
      id: `${SRC.overlaps}-fill`,
      type: 'fill',
      source: SRC.overlaps,
      paint: { 'fill-color': 'rgba(177,74,50,0.35)', 'fill-outline-color': tokens.color.danger },
    })

    map.addSource(SRC.preview, { type: 'geojson', data: EMPTY })
    map.addLayer({
      id: `${SRC.preview}-line`,
      type: 'line',
      source: SRC.preview,
      paint: { 'line-color': tokens.color.brandTeal, 'line-width': 2, 'line-dasharray': [3, 2] },
    })

    map.addSource(SRC.vertices, { type: 'geojson', data: EMPTY })
    map.addLayer({
      id: `${SRC.vertices}-circle`,
      type: 'circle',
      source: SRC.vertices,
      paint: {
        'circle-radius': 4,
        'circle-color': '#ffffff',
        'circle-stroke-color': tokens.color.warning,
        'circle-stroke-width': 1.5,
      },
    })

    map.addSource(SRC.errors, { type: 'geojson', data: EMPTY })
    map.addLayer({
      id: `${SRC.errors}-circle`,
      type: 'circle',
      source: SRC.errors,
      paint: {
        'circle-radius': 10,
        'circle-color': 'rgba(177,74,50,0.12)',
        'circle-stroke-color': tokens.color.danger,
        'circle-stroke-width': 1.5,
      },
    })

    setReady(true)
  }, [])

  // The map goes while the next plot loads (the skeleton replaces it) and a
  // new one is made after. Until then nothing may call into the old one.
  const onRemove = useCallback(() => {
    mapRef.current = null
    setReady(false)
  }, [])

  const setData = (id: string, data: GeoJSON.FeatureCollection) => {
    const source = mapRef.current?.getSource(id)
    if (source) (source as GeoJSONSource).setData(data)
  }

  useEffect(() => {
    if (!ready || !detail || !plot) return
    const map = mapRef.current!

    setData(SRC.neighbours, detail.neighbours)
    setData(SRC.overlaps, detail.overlaps)

    const subject: GeoJSON.FeatureCollection = plot.geometry
      ? {
          type: 'FeatureCollection',
          features: [{ type: 'Feature', geometry: plot.geometry, properties: { status: plot.status } }],
        }
      : EMPTY
    setData(SRC.subject, subject)

    // Every vertex, so an analyst can see what they are about to repair.
    const vertices: GeoJSON.Feature[] = []
    forEachPosition(plot.geometry, (position) =>
      vertices.push({ type: 'Feature', geometry: { type: 'Point', coordinates: position }, properties: {} }),
    )
    setData(SRC.vertices, { type: 'FeatureCollection', features: vertices })

    const errorPoint = detail.issues.find((i) => i.detail?.error_point)?.detail.error_point
    setData(SRC.errors, errorPoint
      ? { type: 'FeatureCollection', features: [{ type: 'Feature', geometry: errorPoint, properties: {} }] }
      : EMPTY)

    fitToFeatures(map, subject, { top: 80, bottom: 80, left: 80, right: 80 })
  }, [detail, plot, ready])

  useEffect(() => {
    if (!ready) return
    setData(SRC.preview, previewRepair && previewGeometry
      ? { type: 'FeatureCollection', features: [{ type: 'Feature', geometry: previewGeometry, properties: {} }] }
      : EMPTY)
  }, [previewGeometry, previewRepair, ready])

  // Land-use overlay, fetched for the plot's own viewport only.
  useEffect(() => {
    const map = mapRef.current
    if (!ready || !map) return
    map.setLayoutProperty(`${SRC.landuse}-fill`, 'visibility', overlay === 'landuse' ? 'visible' : 'none')
    if (overlay !== 'landuse' || !plot?.geometry) return

    const layerId = run?.config?.layers?.land_use ?? layers.find((l) => l.kind === 'land_use' && l.is_default)?.id
    if (!layerId) return
    const b = map.getBounds()
    const [w, s, e, n] = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]
    let objectUrl: string | null = null
    let cancelled = false
    get<Blob>(`/reference-layers/${layerId}/image.png?bbox=${w},${s},${e},${n}&size=768`)
      .then((png) => {
        if (cancelled) return
        objectUrl = URL.createObjectURL(png)
        ;(map.getSource(SRC.landuse) as ImageSource | undefined)?.updateImage({
          url: objectUrl,
          coordinates: [[w, n], [e, n], [e, s], [w, s]],
        })
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
      // MapLibre has decoded it by now; the blob itself is no longer needed.
      if (objectUrl) setTimeout(() => URL.revokeObjectURL(objectUrl!), 1000)
    }
  }, [overlay, ready, plot?.geometry, run?.config, layers])

  // "Preview repair" asks the server for the same geometry the fix would store.
  useEffect(() => {
    if (!previewRepair || !plotId || !geometryErrorIssue) {
      setPreviewGeometry(null)
      setPreviewDelta(null)
      return
    }
    repair.mutate(
      { plotId, preview: true },
      {
        onSuccess: (r) => {
          setPreviewGeometry((r.geometry as GeoJSON.Geometry) ?? null)
          setPreviewDelta(r.delta_ha ?? null)
        },
      },
    )
    // `repair` is a stable mutation object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewRepair, plotId, geometryErrorIssue?.id])

  // The dataset crumb is the way back to the results this plot came from.
  const crumbs = [
    <HomeCrumb key="a" />,
    <Crumb key="b" mono to={runId ? backTo : undefined}>
      {plot?.filename ?? run?.filename ?? ''}
    </Crumb>,
    <Crumb key="c" mono>
      {plot ? <span title={plot.id}>{plot.plot_id ?? '—'} · {shortId(plot.id)}</span> : ''}
    </Crumb>,
  ]


  const missing = new Set<string>(
    (detail?.issues.find((i) => i.check_key === 'null_fields')?.detail?.missing ?? []) as string[],
  )

  const valueOf = (key: AttributeKey): string => {
    if (edits[key] !== undefined) return edits[key]!
    const raw = plot?.[key as keyof typeof plot]
    return raw === null || raw === undefined ? '' : String(raw)
  }

  const dirty = Object.keys(edits).length > 0 || Object.keys(recordEdits).length > 0 || Object.keys(refEdits).length > 0 || Boolean(foCode.trim())

  /** The record a picker shows: the one just chosen, else the plot's own. */
  const recordValue = (which: 'supplier' | 'fo' | 'farmer'): RecordOption | null => {
    if (recordEdits[which] !== undefined) return recordEdits[which]!
    const id = plot?.[`${which}_ref` as const]
    const name = plot?.[`${which}_ref_name` as const]
    return id ? { id, label: name ?? '—', code: which === 'fo' ? plot?.fo_id ?? null : undefined } : null
  }
  // The buttons describe the plot being shown. While the next one loads the
  // previous one is still on screen (`switching`), and its "Include plot" or
  // "Reviewed" belongs to it, not to the plot that is coming.
  const excluded = !switching && plot?.status === 'excluded'
  const reviewed = !switching && Boolean(plot?.reviewed_at)

  // Attribute edits live only in this component until Save.
  useUnsavedWork(dirty, 'This polygon has edits that have not been saved.')

  /** The unsaved attribute edits, as the API takes them. */
  const editedFields = (): Record<string, unknown> => {
    const body: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(edits)) {
      // A field touched and put back as it was is not a change: sending it
      // could only overwrite what was imported.
      const original = plot?.[key as AttributeKey]
      if (String(original ?? '') === value) continue
      body[key] = key === 'yield_kg' ? (value === '' ? null : Number(value)) : value === '' ? null : value
    }
    if (recordEdits.supplier !== undefined) body.supplier_ref = recordEdits.supplier?.id ?? null
    if (recordEdits.fo !== undefined) body.fo_ref = recordEdits.fo?.id ?? null
    if (recordEdits.farmer !== undefined) body.farmer_ref = recordEdits.farmer?.id ?? null
    if (Object.keys(refEdits).length) body.refs = refEdits
    if (foCode.trim()) body.fo_code = foCode.trim()
    return body
  }
  // A request still in flight for a plot already left behind does not hold
  // up the one on screen.
  const busyHere = (m: { isPending: boolean; variables?: { plotId?: string } }) =>
    m.isPending && m.variables?.plotId === plotId

  // Which group is being reviewed, by the names the summary panel uses.
  const groupLabel = checkFilter.map((k) => run?.summary?.checks[k]?.label ?? k).join(', ')
  const toggleCheck = (key: string) => {
    const next = checkFilter.includes(key) ? checkFilter.filter((k) => k !== key) : [...checkFilter, key]
    setParams(next.length ? { check: next.join(',') } : {}, { replace: true })
  }

  const actions = (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<ArrowLeft size={14} strokeWidth={1.5} />}
        onClick={() => goBack(backTo)}
        sx={{ mr: 3 }}
      >
        {runId ? 'Region' : 'Back'}
      </Button>
      <Typography variant="body2" sx={{ color: 'text.secondary', mr: 2 }}>
        {groupLabel && `${groupLabel} · `}
        {!runId ? '' : index >= 0 ? `${num(index + 1)} of ${num(orderedPlots.length)}` : `${num(orderedPlots.length)} to review`}
      </Typography>
      <Button size="small" variant="outlined" disabled={index <= 0} onClick={() => goTo(-1)}>Previous</Button>
      <Button size="small" variant="outlined" disabled={index < 0 || index >= orderedPlots.length - 1} onClick={() => goTo(1)} sx={{ ml: 2 }}>
        Next
      </Button>
    </>
  )

  if (isLoading || !plot || !detail) {
    // Was an empty box, which is indistinguishable from a plot that failed
    // to load.
    return <AppShell crumbs={crumbs} rail={false}><SkeletonScreen wide /></AppShell>
  }

  return (
    <AppShell crumbs={crumbs} actions={actions} rail={false}>
      <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* The groups stay on the left while reviewing, with their counts, so
            the queue can be switched without going back to the region. */}
        {run?.summary && (
          <RunSummaryPanel run={run} activeChecks={checkFilter} onToggleCheck={toggleCheck} />
        )}
        <MapCanvas onReady={onReady} onRemove={onRemove} basemap={overlay === 'satellite' ? 'satellite' : 'map'}>
          <BasemapSwitch
            value={overlay}
            onChange={setOverlay}
            options={[
              { value: 'map', label: 'Basemap' },
              { value: 'satellite', label: 'Satellite' },
              { value: 'landuse', label: 'Land use' },
            ]}
            sx={{ left: 16, right: 'auto' }}
          />

          {geometryErrorIssue && (
            <MapControl
              sx={{ top: 16, right: 16, alignItems: 'center', gap: 2, px: 2.5, py: 1.5, font: `500 12px ${tokens.font.sans}` }}
            >
              <Box
                component="button"
                type="button"
                role="switch"
                aria-checked={previewRepair}
                aria-label="Preview repair"
                onClick={() => setPreviewRepair((v) => !v)}
                sx={{
                  width: 28, height: 16, border: 0, p: 0, cursor: 'pointer', position: 'relative',
                  borderRadius: tokens.radius.pill,
                  backgroundColor: previewRepair ? tokens.color.brandDeep : tokens.color.ink300,
                  transition: `background ${tokens.motion.dur.fast}ms ${tokens.motion.easeStd}`,
                }}
              >
                <Box
                  sx={{
                    position: 'absolute', top: 2, left: previewRepair ? 14 : 2, width: 12, height: 12,
                    borderRadius: '50%', backgroundColor: tokens.color.ink0,
                    transition: `left ${tokens.motion.dur.fast}ms ${tokens.motion.easeStd}`,
                  }}
                />
              </Box>
              Preview repair
            </MapControl>
          )}

          {/* Error and overlap callouts, as in the mockup */}
          <Box sx={{ position: 'absolute', left: 16, bottom: 56, display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'flex-start' }}>
            {geometryErrorIssue && (
              <Callout tone="danger">
                {(geometryErrorIssue.detail?.problems ?? []).includes('invalid') ? 'Self-intersection' : 'Geometry error'}
              </Callout>
            )}
            {detail.overlaps.features.map((f, i) => (
              <Callout key={i} tone="warning">
                Overlap {num((f.properties as any)?.overlap_ha, 2)} ha with {(f.properties as any)?.related_ref ?? 'a neighbour'}
              </Callout>
            ))}
            {previewRepair && previewDelta !== null && (
              <Callout tone="info">
                Preview · area changes by {previewDelta >= 0 ? '+' : '−'}{num(Math.abs(previewDelta), 2)} ha
              </Callout>
            )}
          </Box>
        </MapCanvas>

        <Box
          component="aside"
          sx={{
            width: 400, flex: 'none', backgroundColor: 'background.paper',
            borderLeft: `1px solid ${tokens.color.ink200}`, display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}
        >
          <Box sx={{ px: 6, py: 5, borderBottom: `1px solid ${tokens.color.ink200}` }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Eyebrow>Plot</Eyebrow>
              <StatusPill
                status={plot.status}
                label={`${num(detail.issues.length)} issue${detail.issues.length === 1 ? '' : 's'} · ${plot.status}`}
              />
            </Box>
            <Mono sx={{ display: 'block', fontSize: 18, fontWeight: 600, mt: 1.5, letterSpacing: '-0.01em' }}>
              {plot.plot_id ?? '—'}
              {/* The plot's own id, short: duplicates share a Plot_ID, and
                  without this stepping from one to the next looked like
                  nothing had happened. */}
              <Box component="span" title={plot.id} sx={{ ml: 2, fontSize: 13, fontWeight: 400, color: 'text.secondary' }}>
                {shortId(plot.id)}
              </Box>
            </Mono>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
              {ha(plot.area_ha)} · {num(plot.vertex_count)} vertices
              {plot.supplier_name ? ` · ${plot.supplier_name}` : ''}
            </Typography>
          </Box>

          <Box sx={{ px: 6, py: 4, borderBottom: `1px solid ${tokens.color.ink200}`, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Eyebrow>Issues</Eyebrow>
            {detail.issues.length === 0 && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                No open issues. This plot is ready for a region.
              </Typography>
            )}
            {detail.issues.map((issue) => (
              <Box key={issue.id} sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
                <Box sx={{ color: issue.tier === 'compliance' ? tokens.color.danger : tokens.color.warning, mt: 0.5, flex: 'none' }}>
                  <TriangleAlert size={16} strokeWidth={1.5} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ font: `500 14px ${tokens.font.sans}` }}>
                    {run?.summary?.checks[issue.check_key]?.label ?? issue.check_key}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                    {issue.message}
                  </Typography>
                </Box>
                {issue.check_key === 'geometry_errors' && issue.detail?.repairable && (
                  <Button
                    size="small"
                    startIcon={<Wrench size={12} strokeWidth={1.5} />}
                    disabled={repair.isPending}
                    onClick={() =>
                      repair.mutate({ plotId: plotId! }, { onSuccess: () => { setPreviewRepair(false); setNotice('Repaired. Re-running checks for this plot…') } })
                    }
                    sx={{ height: 28, flex: 'none' }}
                  >
                    Repair
                  </Button>
                )}
                {(issue.check_key === 'partial_overlap' || (issue.check_key === 'duplicates' && issue.detail?.kind === 'geometry')) && issue.related_plot_id && (
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={trim.isPending}
                    onClick={() =>
                      trim.mutate(
                        { plotId: plotId!, against: 'plot', relatedPlotId: issue.related_plot_id! },
                        { onSuccess: () => setNotice(`Trimmed against ${issue.related_ref ?? 'the neighbour'}. Re-running checks for 2 plots…`) },
                      )
                    }
                    sx={{ height: 28, flex: 'none' }}
                  >
                    Trim
                  </Button>
                )}
                {issue.check_key === 'within_country' && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ScissorsLineDashed size={12} strokeWidth={1.5} />}
                    disabled={trim.isPending}
                    onClick={() =>
                      trim.mutate({ plotId: plotId!, against: 'country' }, { onSuccess: () => setNotice('Clipped to the border. Re-running checks…') })
                    }
                    sx={{ height: 28, flex: 'none' }}
                  >
                    Clip
                  </Button>
                )}
              </Box>
            ))}
          </Box>

          <Box sx={{ px: 6, py: 4, flex: 1, overflowY: 'auto' }}>
            <Eyebrow sx={{ mb: 1.5 }}>Attributes</Eyebrow>
            {/* One hairline per row, drawn by the row itself. Bordering each
                cell instead lets the two columns disagree, because a label and
                a 30px input do not settle at the same height. */}
            <Box>
              {ATTRIBUTES.map(({ field, key }, i) => {
                const isMissing = missing.has(field)
                const last = i === ATTRIBUTES.length - 1
                return (
                  <Box
                    key={field}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 3,
                      minHeight: 40,
                      borderBottom: last ? 0 : `1px solid ${tokens.color.ink200}`,
                    }}
                  >
                    <AttributeName
                      field={field}
                      sx={{
                        width: 96, flex: 'none', fontSize: 12,
                        color: isMissing ? tokens.color.warning : 'text.secondary',
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0, py: 0.5 }}>
                      {/* Supplier, farmer organisation and farmer are records:
                          chosen, not typed. FO_ID is the chosen organisation's. */}
                      {key === 'su' ? (
                        <SupplierPicker
                          value={recordValue('supplier')} warn={isMissing} countryCode={plot.country_code}
                          onChange={(o) => setRecordEdits((cur) => ({ ...cur, supplier: o }))}
                        />
                      ) : key === 'fo' ? (
                        <FoPicker
                          value={recordValue('fo')} warn={isMissing} countryCode={plot.country_code}
                          supplierId={recordValue('supplier')?.id}
                          onChange={(o) => setRecordEdits((cur) => ({ ...cur, fo: o }))}
                        />
                      ) : key === 'farmer_id' ? (
                        <FarmerPicker
                          value={recordValue('farmer')} warn={isMissing}
                          supplierId={recordValue('supplier')?.id} foId={recordValue('fo')?.id}
                          onChange={(o) => setRecordEdits((cur) => ({ ...cur, farmer: o }))}
                        />
                      ) : key === 'fo_id' && recordValue('fo') && !recordValue('fo')?.code ? (
                        // FO_ID is the farmer organisation's code; one without a code
                        // can be given it here, and so can every plot of it (AG-91).
                        <Box>
                          <TextField
                            fullWidth
                            size="small"
                            value={foCode}
                            placeholder={`FO_ID for ${recordValue('fo')!.label}`}
                            inputProps={{ 'aria-label': 'FO_ID', maxLength: 100 }}
                            InputProps={{ sx: { fontFamily: tokens.font.mono, fontSize: 13 } }}
                            sx={isMissing ? { '& fieldset': { borderColor: tokens.color.warning } } : undefined}
                            onChange={(e) => setFoCode(e.target.value)}
                          />
                          <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', px: 1.25 }}>
                            Saved on the organisation, for every plot of it.
                          </Typography>
                        </Box>
                      ) : key === 'fo_id' ? (
                        <Mono sx={{ display: 'block', fontSize: 13, px: 1.25, color: recordValue('fo')?.code ? 'text.primary' : 'text.disabled' }}>
                          {recordValue('fo')?.code ?? '—'}
                        </Mono>
                      ) : (
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        type={key === 'yield_kg' ? 'number' : 'text'}
                        value={valueOf(key)}
                        placeholder={isMissing ? (field === 'Certificate' ? 'e.g. RA-CI-2025-0412' : `Enter ${field}`) : ''}
                        inputProps={{ 'aria-label': field }}
                        onChange={(e) => setEdits((cur) => ({ ...cur, [key]: e.target.value }))}
                        InputProps={{
                          endAdornment: key === 'yield_kg' ? (
                            <Typography variant="caption" sx={{ color: 'text.disabled' }}>kg</Typography>
                          ) : undefined,
                          sx: {
                            height: 30,
                            fontFamily: ['fo_id', 'farmer_id', 'plot_id'].includes(key) ? tokens.font.mono : tokens.font.sans,
                            fontSize: 13,
                            '& input': { height: 28, px: 1.25 },
                            '& fieldset': { borderColor: isMissing ? tokens.color.warning : 'transparent' },
                            '&:hover fieldset': { borderColor: isMissing ? tokens.color.warning : tokens.color.ink300 },
                          },
                        }}
                      />
                      )}
                      {/* The integrator's own id for the supplier, when it syncs them. */}
                      {key === 'su' && plot.supplier_ref_external && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', px: 1.25 }}>
                          Your id: {plot.supplier_ref_external}
                        </Typography>
                      )}
                      {/* What the file said, where it differs from the record now. */}
                      {plot.as_filed && field in plot.as_filed && (() => {
                        const filed = plot.as_filed[field as keyof NonNullable<typeof plot.as_filed>]
                        const now = plot[key as 'su' | 'fo' | 'fo_id' | 'farmer_id']
                        return filed && filed.trim() !== (now ?? '').trim() ? (
                          <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', px: 1.25 }}>
                            As filed: {filed}
                          </Typography>
                        ) : null
                      })()}
                    </Box>
                  </Box>
                )
              })}
            </Box>

            <Box sx={{ mt: 3 }}>
              <ReferenceFields
                dense
                values={Object.fromEntries(
                  Object.entries({ ...(plot.refs ?? {}), ...refEdits }).map(([k, v]) => [k, v ?? '']),
                )}
                onChange={(key, value) => setRefEdits((r) => {
                  const next = { ...r }
                  // Back to what is saved: not an edit any more.
                  if ((plot.refs?.[key] ?? null) === value) delete next[key]
                  else next[key] = value
                  return next
                })}
              />
            </Box>

            <PlotComments plotId={plot.id} runId={runId} comments={detail.comments ?? []} />
          </Box>

          <Box
            sx={{
              px: 6, py: 3.5, borderTop: `1px solid ${tokens.color.ink200}`,
              display: 'flex', gap: 2, justifyContent: 'space-between', alignItems: 'center',
            }}
          >
            <Button
              variant="text"
              sx={{ color: tokens.color.danger }}
              disabled={busyHere(exclude) || switching}
              onClick={() => {
                if (excluded) {
                  // Putting one back is a correction: stay and look at it.
                  exclude.mutate({ plotId: plotId!, undo: true }, { onSuccess: () => setNotice('Plot put back.') })
                  return
                }
                const id = plotId!
                actAndMoveOn('Plot excluded.', () => exclude.mutateAsync({ plotId: id, reason: 'Excluded during review' }))
              }}
            >
              {excluded ? 'Include plot' : 'Exclude plot'}
            </Button>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                disabled={busyHere(update) || switching}
                onClick={() => {
                  // Any unsaved edits go in the same request.
                  const id = plotId!
                  const body = editedFields()
                  actAndMoveOn('Marked reviewed.', () => update.mutateAsync({ plotId: id, ...body, reviewed: true }))
                }}
              >
                {reviewed ? 'Reviewed' : 'Mark reviewed'}
              </Button>
              <Button
                disabled={busyHere(update) || switching}
                onClick={() => {
                  if (!dirty) {
                    if (leave()) navigate(nextStop())
                    return
                  }
                  const id = plotId!
                  const body = editedFields()
                  actAndMoveOn('Saved.', () => update.mutateAsync({ plotId: id, ...body }))
                }}
              >
                Save &amp; next
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={4000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" variant="outlined" sx={{ backgroundColor: 'background.paper' }}>{notice}</Alert>
      </Snackbar>
    </AppShell>
  )
}

function Callout({ tone, children }: { tone: 'danger' | 'warning' | 'info'; children: React.ReactNode }) {
  const colour =
    tone === 'danger' ? tokens.color.danger : tone === 'warning' ? tokens.color.warning : tokens.color.brandTeal
  return (
    <Box
      sx={{
        px: 2, py: 1, borderRadius: `${tokens.radius.sm}px`, backgroundColor: 'background.paper',
        border: `1px solid ${tokens.color.ink200}`, boxShadow: tokens.shadow[2],
        font: `500 11px ${tokens.font.sans}`, color: colour, whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Box>
  )
}

/** The first block of a uuid: enough to tell plots apart at a glance. */
function shortId(id: string): string {
  return id.slice(0, 8)
}
